import { ref, watch, type Ref } from 'vue'

/**
 * Viewer-side player preferences persisted globally (not per-asset/embed) in
 * localStorage, so a choice made on one video carries over to the next.
 *
 * Holds the subtitle size multiplier and the transcript panel's text size; the
 * structure is intentionally extensible to host future preferences (volume, speed, …).
 */

const STORAGE_KEY = 'videas:player:prefs'

export interface SubtitleScalePreset {
  /** Multiplier applied on top of the container-relative auto-scaling. */
  value: number
  /** i18n key suffix under `player.settings.subtitleSizes`. */
  labelKey: string
}

// `1` reproduces the historical (auto) subtitle size exactly. The other steps
// nudge it down/up while the existing RGAA floor (14px) is preserved downstream.
export const SUBTITLE_SCALE_PRESETS: readonly SubtitleScalePreset[] = [
  { value: 0.75, labelKey: 'small' },
  { value: 1, labelKey: 'normal' },
  { value: 1.5, labelKey: 'large' },
] as const

const DEFAULT_SUBTITLE_SCALE = 1

/** The transcript panel's text sizes, in rem, smallest first; the first is its historical size and the default. */
export const TRANSCRIPT_TEXT_SIZES: readonly number[] = [0.8125, 0.9375, 1.0625] as const

const DEFAULT_TRANSCRIPT_SIZE = 0

interface StoredPrefs {
  subtitleScale?: number
  /** An index into {@link TRANSCRIPT_TEXT_SIZES}. */
  transcriptSize?: number
}

function readPrefs(): StoredPrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    return JSON.parse(raw) as StoredPrefs
  } catch {
    return {}
  }
}

function writePrefs(prefs: StoredPrefs): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs))
  } catch {
    // localStorage full or unavailable — preferences simply won't persist
  }
}

function isValidScale(value: unknown): value is number {
  return typeof value === 'number' && SUBTITLE_SCALE_PRESETS.some((p) => p.value === value)
}

function isValidTranscriptSize(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value < TRANSCRIPT_TEXT_SIZES.length
}

export interface UsePlayerPreferencesReturn {
  subtitleScale: Ref<number>
  setSubtitleScale: (scale: number) => void
  /** An index into {@link TRANSCRIPT_TEXT_SIZES}. */
  transcriptSize: Ref<number>
  setTranscriptSize: (size: number) => void
}

// Module-level singleton state. The preference is global, so every
// usePlayerPreferences() consumer shares the SAME reactive ref: a change in one
// mounted player is reflected live in any other, and there is exactly one
// persistence watcher (no per-instance write races).
function readInitialScale(): number {
  const stored = readPrefs()
  return isValidScale(stored.subtitleScale) ? stored.subtitleScale : DEFAULT_SUBTITLE_SCALE
}

const subtitleScale = ref<number>(readInitialScale())

function setSubtitleScale(scale: number): void {
  if (!isValidScale(scale)) return
  subtitleScale.value = scale
}

watch(subtitleScale, (value) => {
  writePrefs({ ...readPrefs(), subtitleScale: value })
})

function readInitialTranscriptSize(): number {
  const stored = readPrefs()
  return isValidTranscriptSize(stored.transcriptSize) ? stored.transcriptSize : DEFAULT_TRANSCRIPT_SIZE
}

const transcriptSize = ref<number>(readInitialTranscriptSize())

function setTranscriptSize(size: number): void {
  if (!isValidTranscriptSize(size)) return
  transcriptSize.value = size
}

watch(transcriptSize, (value) => {
  writePrefs({ ...readPrefs(), transcriptSize: value })
})

// Stay in sync when another tab/window changes the preference. The equality
// guard prevents a write-back loop (a `storage` event only fires in the other
// documents, never the one that wrote it).
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return
    const stored = readPrefs()
    if (isValidScale(stored.subtitleScale) && stored.subtitleScale !== subtitleScale.value) {
      subtitleScale.value = stored.subtitleScale
    }
    if (isValidTranscriptSize(stored.transcriptSize) && stored.transcriptSize !== transcriptSize.value) {
      transcriptSize.value = stored.transcriptSize
    }
  })
}

export function usePlayerPreferences(): UsePlayerPreferencesReturn {
  return { subtitleScale, setSubtitleScale, transcriptSize, setTranscriptSize }
}
